/**
 * `useToday()` — the kit's ONE source of "today" (#2780).
 *
 * Returns the calendar day (`YYYY-MM-DD`) in the operating time zone. The zone
 * comes from the host's `format.timeZone` seam; the arithmetic is the kit's own
 * `calendarDayIn` (`util/tz.ts`), so no host module is read.
 *
 * Every reader of the day a list FILTERS ON — the list controller that hands
 * the day to `FilterPreset.buildConditions(today)`, and any date-dependent
 * preset adornment or list banner — MUST read it through this hook and MUST
 * NOT read the clock itself. (A timestamp that only names something, such as
 * an export file name, is not such a reader.) All readers of one zone
 * subscribe to the same module-level store and read the same snapshot, which
 * is what keeps a preset's list and its count on the same day.
 *
 * The snapshot is the store's CACHED day, not a fresh clock read. React
 * re-reads a snapshot whenever a reader re-renders, for any reason; a fresh
 * read would let the first reader to re-render after midnight (a preset count
 * after a refetch) move to the new day while the list stays on the old one
 * until the next wake. The cache is refreshed only by a wake, and readers are
 * notified only when the day actually changed, so every reader moves in the
 * same update. With no subscriber there is nothing to keep consistent with,
 * so the snapshot re-reads the clock; the first subscriber re-reads it too.
 *
 * The store wakes from three sources, and each is needed:
 *   - a 60 s interval: a laptop that sleeps with the tab in the foreground
 *     wakes with no `visibilitychange` and maybe no `focus`, so only a tick
 *     moves the day forward (at most 60 s late);
 *   - `visibilitychange` to visible: the tab comes back to the front;
 *   - window `focus`: the window comes back to the front.
 * A wake on an unchanged day notifies no one. An exact-midnight
 * `setTimeout` was rejected: a sleeping machine can fire it long after
 * waking, and a foreground tab may get no event when it wakes.
 *
 * `useSyncExternalStore` (precedent: `useKitIsPhone` in the ui package) gives
 * every subscriber the same snapshot in one update, and re-renders at once a
 * subscriber whose snapshot changed between render and commit.
 *
 * The store is module-level, one per zone. When the last subscriber leaves,
 * the interval and both event listeners are removed, so nothing is left
 * running after the readers unmount (tests included).
 */
import { useSyncExternalStore } from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import { calendarDayIn } from '../util/tz';

const TICK_MS = 60_000;

interface DayStore {
  subscribe: (onChange: () => void) => () => void;
  getSnapshot: () => string;
}

const stores = new Map<string, DayStore>();

function createDayStore(timeZone: string): DayStore {
  const listeners = new Set<() => void>();
  let interval: ReturnType<typeof setInterval> | undefined;
  const readClock = () => calendarDayIn(timeZone, new Date(Date.now()));
  let day = readClock();
  const refresh = () => {
    const next = readClock();
    if (next === day) return;
    day = next;
    // Notify a copy: a listener that subscribes or unsubscribes while being
    // notified must not change who this wake reaches.
    for (const listener of Array.from(listeners)) listener();
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') refresh();
  };
  return {
    subscribe(onChange) {
      listeners.add(onChange);
      if (listeners.size === 1) {
        day = readClock();
        interval = setInterval(refresh, TICK_MS);
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', refresh);
      }
      return () => {
        listeners.delete(onChange);
        if (listeners.size === 0) {
          clearInterval(interval);
          interval = undefined;
          document.removeEventListener('visibilitychange', onVisibility);
          window.removeEventListener('focus', refresh);
        }
      };
    },
    getSnapshot: () => {
      if (listeners.size === 0) day = readClock();
      return day;
    },
  };
}

function dayStoreFor(timeZone: string): DayStore {
  let store = stores.get(timeZone);
  if (store === undefined) {
    store = createDayStore(timeZone);
    stores.set(timeZone, store);
  }
  return store;
}

export function useToday(): string {
  const { format } = useResourceKit();
  const store = dayStoreFor(format.timeZone);
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
